import React from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CardDefinition,
  CardRarity,
  LobbyEntryTerms,
  LobbyParticipant,
  LobbyPullNotYourTurn,
  LobbySnapshot,
  LobbyTimingZone,
  useGetLobbyTodayQuery,
  useJoinLobbyMutation,
  usePullCardMutation,
} from "../api/respectCardsApi";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { respectCardsApi } from "../api/respectCardsApi";
import { useLoadingMessages } from "../../aotd/hooks/useLoadingMessages";
import CardRevealOverlay from "../components/CardRevealOverlay";
import { CARD_DROP_LOADING_MESSAGES } from "../data/cardDropLoadingMessages";
import { useDropLobbyStream } from "../hooks/useDropLobbyStream";
import { useNowTicker } from "../hooks/useNowTicker";
import { resolveLobbyEntryError } from "../lib/lobbyErrors";
import { getApiErrorMessage } from "../lib/utils";
import "../styles/cards.css";
import "../styles/dropLobby.css";

/** Fallback bubble for participants who haven't picked a display emoji. */
const FALLBACK_EMOJI = "🃏";

const RARITY_ORDER: CardRarity[] = ["legendary", "epic", "rare", "common"];

// ── 4.5s sweet-spot constants (mirror the canonical curve in the backend's
// `LOBBY_MODIFIER_DEFAULTS`). The server is authoritative for the actual roll;
// this only powers the player's own *estimated* timing feedback, since the
// public surfaces deliberately omit the timing multiplier/zone.
const TIMING_TARGET_MS = 4500;
const TIMING_REWARD_WINDOW_MS = 500;
const TIMING_JACKPOT_EPSILON_MS = 1;
const TIMING_PENALTY_FLOOR = 0.5;
const TIMING_PENALTY_SPAN_MS = 3000;
const TIMING_PEAK = 6.6;
const TIMING_EXP = 51;

interface TimingFeedback {
  multiplier: number;
  zone: LobbyTimingZone;
}

function estimateTiming(elapsedMs: number): TimingFeedback {
  const offset = Math.abs(elapsedMs - TIMING_TARGET_MS);
  if (offset <= TIMING_JACKPOT_EPSILON_MS) return { multiplier: 100, zone: "jackpot" };
  if (offset <= TIMING_REWARD_WINDOW_MS) {
    const t = (TIMING_REWARD_WINDOW_MS - offset) / TIMING_REWARD_WINDOW_MS;
    const multiplier = 1 + (TIMING_PEAK - 1) * Math.pow(t, TIMING_EXP);
    return { multiplier, zone: "reward" };
  }
  const over = Math.min(1, (offset - TIMING_REWARD_WINDOW_MS) / TIMING_PENALTY_SPAN_MS);
  const multiplier = Math.max(TIMING_PENALTY_FLOOR, 1 - (1 - TIMING_PENALTY_FLOOR) * over);
  return { multiplier, zone: "penalty" };
}

function pct(chance: number): string {
  return `${Math.round(chance * 1000) / 10}%`;
}

// The AOTD-holder and pull-streak bonuses are retired (the backend sends both
// as a constant 0); the drought bonus took the streak's place — what earns you
// better odds now is entering and walking away empty.
function modifierBadges(p: LobbyParticipant): string[] {
  const badges: string[] = [];
  const m = p.modifiers;
  badges.push(`#${p.arrivalRank + 1} arrival +${Math.round(m.arrivalBonus * 100)}%`);
  if (m.coffeeRatingBonus > 0) badges.push(`☕ +${Math.round(m.coffeeRatingBonus * 100)}%`);
  if (m.creatorBonus > 0) badges.push(`🎨 +${Math.round(m.creatorBonus * 100)}%`);
  if ((m.droughtBonus ?? 0) > 0)
    badges.push(`🌵 dry run +${Math.round((m.droughtBonus ?? 0) * 100)}%`);
  if ((m.cardEffectBonus ?? 0) > 0)
    badges.push(`🃏 +${Math.round((m.cardEffectBonus ?? 0) * 100)}%`);
  if (p.isLateJoiner) badges.push("🕒 late");
  return badges;
}

/**
 * One line telling the viewer what walking in costs them, read straight off the
 * server-quoted `lobby.yourEntry` (losers' bracket, `dropEntryPolicy`). Null
 * when the snapshot carries no terms — logged out, or an SSE frame with no
 * single viewer — so nothing is guessed at.
 */
function entryTermsLine(
  entry: LobbyEntryTerms | null | undefined,
  lane: "window" | "late",
): string | null {
  if (!entry) return null;
  if (entry.role === "loser") {
    return lane === "window"
      ? "You lost today's race, so this round is yours: your pull is free."
      : "You raced today, so a late pull is still free.";
  }
  if (entry.free) return null; // legacy open day — nothing to warn about
  if (entry.lateEntryOnly && lane === "window") {
    return `You didn't race today, so the live window isn't yours. Late entry opens when it closes, for ${entry.fee} respect at reduced odds.`;
  }
  if (entry.role === "winner") {
    return lane === "window"
      ? `You already took the art. Entering the pull as well costs ${entry.fee} respect.`
      : `You already took the art. A late pull costs ${entry.fee} respect.`;
  }
  return `Entering costs ${entry.fee} respect.`;
}

// ── turn timer ─────────────────────────────────────────────────────────────
function TurnTimer({
  turnStartedAt,
  turnWindowMs,
}: {
  turnStartedAt: string | null;
  turnWindowMs: number;
}) {
  useNowTicker(100);
  const startMs = turnStartedAt ? Date.parse(turnStartedAt) : null;
  const elapsed = startMs ? Math.max(0, Date.now() - startMs) : 0;
  const window = turnWindowMs || 10_000;
  const fillPct = Math.min(100, (elapsed / window) * 100);
  const sweetPct = Math.min(100, (TIMING_TARGET_MS / window) * 100);
  const bandStart = Math.max(0, ((TIMING_TARGET_MS - TIMING_REWARD_WINDOW_MS) / window) * 100);
  const bandEnd = Math.min(100, ((TIMING_TARGET_MS + TIMING_REWARD_WINDOW_MS) / window) * 100);

  return (
    <div>
      <div className="drop-lobby__timer-track" aria-label="Turn timer">
        <div className="drop-lobby__timer-fill" style={{ width: `${fillPct}%` }} />
        <div
          className="drop-lobby__timer-band"
          style={{ left: `${bandStart}%`, width: `${bandEnd - bandStart}%` }}
        />
        <div className="drop-lobby__timer-sweet" style={{ left: `${sweetPct}%` }} />
      </div>
      <div className="drop-lobby__timer-elapsed">{(elapsed / 1000).toFixed(2)}s</div>
    </div>
  );
}

// ── countdown to a deadline (gathering) ─────────────────────────────────────
function useSecondsUntil(iso: string | null): number | null {
  useNowTicker(250);
  if (!iso) return null;
  const ms = Date.parse(iso) - Date.now();
  return Math.max(0, Math.ceil(ms / 1000));
}

// ── gathering race (emoji bubbles + live thinning odds) ─────────────────────
// Red-themed sibling of AOTD's RaceWaitingIndicator: a single row of player
// emoji bubbles whose win-chance percentages animate downward as the field
// dilutes (driven by `odds_update` merging winChance into the live roster).
function GatheringRace({
  participants,
  currentUserId,
}: {
  participants: LobbyParticipant[];
  currentUserId: string | null;
}) {
  // Only the gathered field competes for the pool; late joiners pull solo.
  const gathered = participants.filter((p) => !p.isLateJoiner);
  const message = useLoadingMessages(true, CARD_DROP_LOADING_MESSAGES);

  if (gathered.length === 0) {
    return (
      <div className="drop-race">
        <div className="drop-race__message">{message}</div>
      </div>
    );
  }

  return (
    <div className="drop-race">
      <div className="drop-race__legend">your multiplier · chance for the best card</div>
      <div className="drop-race__row">
        {gathered.map((p) => {
          const isYou = p.userId === currentUserId;
          const m = p.multiplier ?? 1;
          const mLabel = `×${m.toFixed(m >= 10 ? 0 : 1)}`;
          const bestRarity = p.bestRarity;
          const bestPct =
            p.bestRarityChance != null
              ? `${Math.round(p.bestRarityChance * 100)}%`
              : null;
          return (
            <div
              key={p.userId}
              className={`drop-race__player ${isYou ? "drop-race__player--you" : ""}`}
              title={`${p.username}${isYou ? " (you)" : ""} — ${mLabel}${bestRarity ? ` · ${bestPct ?? "—"} for ${bestRarity}` : ""}`}
            >
              <span className="drop-race__bubble">{p.userEmoji || FALLBACK_EMOJI}</span>
              <span className="drop-race__pct" data-pct={Math.round(m * 100)}>
                {mLabel}
              </span>
              {bestRarity && bestPct ? (
                <span
                  className={`drop-race__bestrarity drop-race__rar--${bestRarity}`}
                  title={`${bestPct} chance to pull a ${bestRarity}`}
                >
                  {bestPct}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="drop-race__message">{message}</div>
    </div>
  );
}

// ── participant card (odds + result) ────────────────────────────────────────
function ParticipantCard({
  p,
  isCurrent,
  isYou,
}: {
  p: LobbyParticipant;
  isCurrent: boolean;
  isYou: boolean;
}) {
  const cls = [
    "drop-lobby__pcard",
    isCurrent ? "drop-lobby__pcard--current" : "",
    isYou ? "drop-lobby__pcard--you" : "",
    p.pullStatus === "done" ? "drop-lobby__pcard--done" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const slotByRarity = new Map(p.slotChances.map((s) => [s.rarity, s.preTimingChance]));

  return (
    <div className={cls}>
      <div className="drop-lobby__pcard-head">
        <span className={`drop-lobby__pname ${isYou ? "drop-lobby__pname-you" : ""}`}>
          {p.username}
          {isYou ? " (you)" : ""}
        </span>
        <span className="drop-lobby__prank">arrival #{p.arrivalRank + 1}</span>
      </div>

      <div className="drop-lobby__mods">
        {modifierBadges(p).map((b) => (
          <span key={b} className="drop-lobby__mod">
            {b}
          </span>
        ))}
      </div>

      {typeof p.multiplier === "number" ? (
        <div className="drop-lobby__winchance">
          <span className="drop-lobby__winchance-label">your multiplier</span>
          <span className="drop-lobby__winchance-pct">
            ×{p.multiplier.toFixed(p.multiplier >= 10 ? 0 : 2)}
          </span>
        </div>
      ) : null}

      {p.slotChances.length > 0 ? (
        <div className="drop-lobby__odds-legend">chance per rarity</div>
      ) : null}
      <div className="drop-lobby__odds">
        {RARITY_ORDER.filter((r) => slotByRarity.has(r)).map((r) => {
          const chance = slotByRarity.get(r) ?? 0;
          return (
            <div className="drop-lobby__odds-row" key={r}>
              <span className={`drop-lobby__odds-rarity drop-lobby__rar--${r}`}>{r}</span>
              <span className="drop-lobby__odds-bar">
                <span
                  className={`drop-lobby__odds-fill drop-lobby__fill--${r}`}
                  style={{ width: `${Math.min(100, chance * 100)}%` }}
                />
              </span>
              <span className="drop-lobby__odds-pct">{pct(chance)}</span>
            </div>
          );
        })}
      </div>

      {p.pullResult ? (
        <div
          className={`drop-lobby__result ${
            p.pullResult.jackpot
              ? "drop-lobby__result--jackpot"
              : p.pullResult.outcome === "card"
              ? "drop-lobby__result--card"
              : "drop-lobby__result--nothing"
          }`}
        >
          {p.pullResult.jackpot
            ? "🎉 JACKPOT!"
            : p.pullResult.outcome === "card"
            ? `🎴 pulled ${p.pullResult.rarity ?? "a card"}`
            : "— nothing this time"}
        </div>
      ) : null}
    </div>
  );
}

// ── jackpot celebration ──────────────────────────────────────────────────────
function JackpotCelebration({
  username,
  onClose,
}: {
  username: string;
  onClose: () => void;
}) {
  React.useEffect(() => {
    const t = window.setTimeout(onClose, 6000);
    return () => window.clearTimeout(t);
  }, [onClose]);

  return (
    <div className="drop-lobby__jackpot" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="drop-lobby__jackpot-card">
        <div className="drop-lobby__jackpot-emoji">🎰</div>
        <div className="drop-lobby__jackpot-title">JACKPOT</div>
        <div className="drop-lobby__jackpot-sub">
          {username} hit the 4.5s sweet spot — guaranteed best card + jackpot badge!
        </div>
      </div>
    </div>
  );
}

export interface DropLobbyPageProps {
  /**
   * cards-drop-lobby phase 12 — when provided, render this snapshot instead of
   * the live authed query + SSE stream. The dev sim panel uses this to drive
   * the REAL lobby UI against a sim lobby. Read-only: join/pull controls are
   * hidden (the panel advances the lobby via the sim step endpoints).
   */
  simLobby?: LobbySnapshot | null;
  /** Which sim participant the view should treat as "you" (sim mode only). */
  simViewerId?: string | null;
}

export default function DropLobbyPage({ simLobby, simViewerId }: DropLobbyPageProps = {}) {
  const isSim = simLobby !== undefined;
  // In sim mode the panel drives the steps, so the live join/pull actions are
  // suppressed and the page is a pure render of the provided snapshot.
  const interactive = !isSim;

  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const isAuthenticated = isSim
    ? true
    : auth.bootstrapStatus === "ready" && Boolean(auth.token);
  const currentUserId = isSim ? simViewerId ?? null : auth.userId;

  const { data: todayData, refetch } = useGetLobbyTodayQuery(undefined, { skip: isSim });
  const stream = useDropLobbyStream();
  const lobby = isSim ? simLobby : stream.lobby;
  const connected = isSim ? true : stream.connected;
  const refreshKey = stream.refreshKey;
  const jackpotAnim = isSim ? null : stream.jackpotAnim;
  const applySeed = stream.applySeed;

  const [joinLobby] = useJoinLobbyMutation();
  const [pullCard, { isLoading: isPulling }] = usePullCardMutation();

  const [revealed, setRevealed] = React.useState<{
    card: CardDefinition;
    headline?: string;
  } | null>(null);
  const [lastTiming, setLastTiming] = React.useState<TimingFeedback | null>(null);
  const [waiting, setWaiting] = React.useState<LobbyPullNotYourTurn | null>(null);
  const [dismissedJackpot, setDismissedJackpot] = React.useState(0);
  const autoJoinedRef = React.useRef(false);

  React.useEffect(() => {
    if (isSim) return;
    document.title = "NΛSOS — Card Drop";
  }, [isSim]);

  // Seed the live state from the viewer-aware authed snapshot.
  React.useEffect(() => {
    if (isSim) return;
    const seed = todayData?.lobby ?? null;
    if (seed) applySeed(seed);
  }, [isSim, todayData, applySeed]);

  // SSE transitions (gathering close, reveal complete, lobby_state) need the
  // authoritative snapshot (per-participant odds + the freshly armed turn).
  React.useEffect(() => {
    if (isSim) return;
    if (refreshKey > 0) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Auto-join once when the gathering window is OPEN (armed at open, so
  // `gatheringEndsAt` is set) and we haven't joined yet. We deliberately do NOT
  // auto-join an un-armed lobby — the 45s timer is anchored to open, never to
  // the first arrival, so joining must not be what starts the clock.
  //
  // Nor do we auto-join an entry the viewer has to PAY for (the day's winner, a
  // non-racer): landing on the page would silently debit their respect, or bounce
  // them with `race_only`. Those two get a button instead. An absent
  // `yourEntry` (older backend / legacy open day) keeps the old free behaviour.
  React.useEffect(() => {
    if (isSim || autoJoinedRef.current) return;
    if (!isAuthenticated || !lobby || lobby.status !== "gathering") return;
    if (!lobby.gatheringEndsAt) return;
    if (lobby.yourEntry && !lobby.yourEntry.free) return;
    const alreadyIn = lobby.participants.some((p) => p.userId === currentUserId);
    if (alreadyIn || lobby.youAreBlocked) return;
    autoJoinedRef.current = true;
    void handleJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, lobby, currentUserId]);

  async function handleJoin() {
    try {
      const res = await joinLobby().unwrap();
      applySeed(res.lobby);
      if (res.entryFeePaid) {
        toast.info(`You're in. ${res.entryFeePaid} respect paid.`, {
          position: "bottom-right",
        });
      }
    } catch (err) {
      // The losers'-bracket refusals (race_only, entry_fee) carry a code, so
      // map them to copy that says what to do instead. `lib/lobbyErrors.ts`
      // falls back to the server message for anything unmapped.
      toast.error(resolveLobbyEntryError(err).hint, { position: "bottom-right" });
    }
  }

  async function handlePull() {
    setWaiting(null);
    // Estimate our own timing from the locally-measured elapsed (server is
    // authoritative; this is feedback only).
    const startMs = lobby?.currentTurn?.turnStartedAt
      ? Date.parse(lobby.currentTurn.turnStartedAt)
      : null;
    const isMyTurn = lobby?.currentTurn?.userId === currentUserId;
    try {
      const res = await pullCard().unwrap();
      if (!res.success) {
        toast.info(res.message || "No card this time.", { position: "bottom-right" });
        refetch();
        return;
      }
      if (isMyTurn && startMs) {
        setLastTiming(estimateTiming(Math.max(0, Date.now() - startMs)));
      }
      if (res.outcome === "card" && res.card) {
        setRevealed({ card: res.card, headline: "You pulled" });
      } else {
        toast.info(res.message || "No card this time — see you tomorrow.", {
          position: "bottom-right",
        });
      }
      refetch();
    } catch (err) {
      const anyErr = err as { status?: number; data?: LobbyPullNotYourTurn };
      if (anyErr.status === 423 && anyErr.data) {
        setWaiting(anyErr.data);
        return;
      }
      if (anyErr.status === 403) {
        toast.error("You're blocked from this drop.", { position: "bottom-right" });
        return;
      }
      toast.error(getApiErrorMessage(err, "Could not pull your card."), {
        position: "bottom-right",
      });
    }
  }

  const myParticipant = lobby?.participants.find((p) => p.userId === currentUserId) ?? null;
  const isYourTurn = Boolean(
    lobby?.currentTurn && lobby.currentTurn.userId === currentUserId,
  );
  const showJackpot = jackpotAnim && jackpotAnim.seq !== dismissedJackpot;
  // Hooks must run unconditionally — compute the gathering countdown up here.
  const gatheringSecs = useSecondsUntil(lobby?.gatheringEndsAt ?? null);

  // ── render ────────────────────────────────────────────────────────────────
  let body: React.ReactNode;

  if (!isAuthenticated) {
    body = (
      <div className="drop-lobby__panel">
        <p className="drop-lobby__msg">
          <Link to="/login">Log in</Link> to join today&apos;s card drop.
        </p>
      </div>
    );
  } else if (!connected && !lobby) {
    // Distinct connecting state — NOT the gathering loader. We have no snapshot
    // yet (initial connect, or a refresh dropped the SSE) so we can't tell if a
    // drop is even live; say so plainly so it never reads as "gathering".
    body = (
      <div className="drop-lobby__panel drop-lobby__connecting">
        <div className="drop-lobby__connecting-spinner" aria-hidden="true">
          🔌
        </div>
        <div className="drop-lobby__connecting-title">Linking you to the drop…</div>
        <p className="drop-lobby__msg">Hang on — opening the live channel.</p>
      </div>
    );
  } else if (!lobby) {
    body = (
      <div className="drop-lobby__panel">
        <p className="drop-lobby__msg">
          No drop happening right now. Pull from the <Link to="/cards">daily deck</Link>{" "}
          instead, or wait for the next drop notification.
        </p>
      </div>
    );
  } else if (lobby.youAreBlocked) {
    body = (
      <div className="drop-lobby__panel">
        <p className="drop-lobby__msg">🚫 You&apos;re blocked from today&apos;s drop.</p>
      </div>
    );
  } else if (lobby.status === "gathering") {
    const secs = gatheringSecs;
    const entry = lobby.yourEntry ?? null;
    const entryLine = entryTermsLine(entry, "window");
    // A non-racer can't enter the live window at all (the join answers
    // `race_only`), so we tell them rather than offering a button that fails.
    const windowIsTheirs = !entry?.lateEntryOnly;
    body = (
      <div className="drop-lobby__panel drop-lobby__gather drop-lobby__gather--race">
        <div className="drop-lobby__eyebrow drop-lobby__eyebrow--red">Cards are open</div>
        <div className="drop-lobby__gather-title">🃏 The drop is gathering!</div>
        <div className="drop-lobby__count drop-lobby__count--red">
          {lobby.participantCount}
        </div>
        <div className="drop-lobby__count-label">
          {lobby.participantCount === 1 ? "player" : "players"} in the lobby — the more
          who join, the thinner everyone&apos;s odds
        </div>
        {lobby.gatheringEndsAt ? (
          <div className="drop-lobby__gather-timer">
            Gathering closes in <strong>{secs ?? "…"}s</strong>
          </div>
        ) : (
          <div className="drop-lobby__gather-timer">The drop is opening — hang tight…</div>
        )}

        <GatheringRace participants={lobby.participants} currentUserId={currentUserId} />

        {!myParticipant && entryLine ? (
          <p className="drop-lobby__msg">{entryLine}</p>
        ) : null}

        {myParticipant ? (
          <p className="drop-lobby__msg">
            You&apos;re in (arrival #{myParticipant.arrivalRank + 1}). Hang tight — pulls
            begin when gathering closes.
          </p>
        ) : interactive && windowIsTheirs ? (
          <button type="button" className="drop-lobby__btn drop-lobby__btn--red" onClick={handleJoin}>
            {entry && !entry.free
              ? `✨ Buy in for ${entry.fee} respect`
              : "✨ Join the drop"}
          </button>
        ) : null}
      </div>
    );
  } else {
    // revealing OR done — show the roster (everyone's odds + results).
    const current = lobby.currentTurn;
    const remaining =
      lobby.remainingSupply ??
      (lobby.pool ? lobby.pool.reduce((s, e) => s + e.remaining, 0) : null);
    // Late entry stays open while cards remain (server-authoritative flag,
    // with a local fallback for stale snapshots).
    const lateOpen =
      lobby.lateEntryOpen ?? (remaining == null || remaining > 0);
    const lateJoinerCanPull =
      interactive &&
      myParticipant &&
      myParticipant.isLateJoiner &&
      myParticipant.pullStatus !== "done";
    const canJoinLate = interactive && !myParticipant && lateOpen;
    const penaltyPct = Math.round((lobby.lateEntryPenalty ?? 0.6) * 100);
    const lateEntry = lobby.yourEntry ?? null;
    const lateEntryLine = entryTermsLine(lateEntry, "late");
    const queuePos = waiting
      ? waiting.queuePosition
      : myParticipant
      ? Math.max(0, myParticipant.arrivalRank - lobby.revealIndex)
      : 0;

    body = (
      <>
        {lobby.status === "revealing" && current ? (
          <div className="drop-lobby__panel drop-lobby__turn">
            <div className="drop-lobby__eyebrow">Now revealing</div>
            <div className="drop-lobby__turn-who">
              {isYourTurn ? (
                <>It&apos;s <strong>your</strong> turn — pull near 4.5s!</>
              ) : (
                <>
                  Waiting for <strong>{current.username}</strong>
                </>
              )}
            </div>
            <TurnTimer
              turnStartedAt={current.turnStartedAt}
              turnWindowMs={lobby.turnWindowMs}
            />
            {isYourTurn && interactive ? (
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="drop-lobby__btn drop-lobby__btn--pull"
                  onClick={handlePull}
                  disabled={isPulling}
                >
                  {isPulling ? "Pulling…" : "🎴 Pull now"}
                </button>
              </div>
            ) : !isYourTurn &&
              myParticipant &&
              !myParticipant.isLateJoiner &&
              myParticipant.pullStatus !== "done" ? (
              <div className="drop-lobby__waiting">
                <div className="drop-lobby__queue">#{queuePos}</div>
                <p className="drop-lobby__msg">
                  in the queue — waiting for {current.username}
                </p>
              </div>
            ) : null}
            {lastTiming ? (
              <div
                className={`drop-lobby__timing-readout drop-lobby__timing-readout--${lastTiming.zone}`}
              >
                ⏱ ~×{lastTiming.multiplier.toFixed(1)} {lastTiming.zone}
                {lastTiming.zone === "reward" ? " — nice timing!" : ""}
              </div>
            ) : null}
          </div>
        ) : null}

        {canJoinLate || lateJoinerCanPull ? (
          <div className="drop-lobby__panel" style={{ textAlign: "center" }}>
            <div className="drop-lobby__eyebrow">Late to the party?</div>
            <p className="drop-lobby__msg">
              {remaining != null
                ? `${remaining} of today's cards ${remaining === 1 ? "is" : "are"} still unclaimed. `
                : "Some of today's cards may still be unclaimed. "}
              The race is over, so a late pull runs at {penaltyPct}% of the
              usual odds — a long shot, not a lockout.
            </p>
            {canJoinLate && lateEntryLine ? (
              <p className="drop-lobby__msg">{lateEntryLine}</p>
            ) : null}
            {lateJoinerCanPull ? (
              <button
                type="button"
                className="drop-lobby__btn drop-lobby__btn--pull"
                onClick={handlePull}
                disabled={isPulling}
              >
                {isPulling ? "Pulling…" : "🎴 Pull (late)"}
              </button>
            ) : (
              <button
                type="button"
                className="drop-lobby__btn drop-lobby__btn--red"
                onClick={handleJoin}
              >
                {lateEntry && !lateEntry.free
                  ? `🃏 Join late for ${lateEntry.fee} respect`
                  : "🃏 Join late"}
              </button>
            )}
          </div>
        ) : null}

        {lobby.status === "done" && !lateOpen ? (
          <div className="drop-lobby__panel" style={{ textAlign: "center" }}>
            <p className="drop-lobby__msg">
              🎴 All of today&apos;s cards are claimed — see you at the next drop.
            </p>
          </div>
        ) : null}

        <div className="drop-lobby__panel">
          <div className="drop-lobby__eyebrow">
            {lobby.status === "done" ? "Drop complete" : "Everyone's odds"}
          </div>
          {remaining != null && lobby.totalSupply != null ? (
            <div className="drop-lobby__count-label">
              {lobby.totalSupply - remaining}/{lobby.totalSupply} cards claimed
            </div>
          ) : null}
          <div className="drop-lobby__roster">
            {lobby.participants.map((p) => (
              <ParticipantCard
                key={p.userId}
                p={p}
                isCurrent={current?.userId === p.userId}
                isYou={p.userId === currentUserId}
              />
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="drop-lobby-page-shell">
    <div className="drop-lobby">
      <header className="drop-lobby__header">
        <Link to="/cards" className="drop-lobby__back">
          ← Cards
        </Link>
        <h1>Card Drop</h1>
        <span
          className={`drop-lobby__conn ${connected ? "drop-lobby__conn--live" : ""}`}
        >
          {connected ? "● live" : "offline"}
        </span>
      </header>

      {/* Reconnecting (SSE dropped but we still have a snapshot) — distinct
          from the gathering loader so a flaky link never reads as "gathering". */}
      {!connected && lobby ? (
        <div className="drop-lobby__reconnect" role="status">
          🔌 Reconnecting to the drop…
        </div>
      ) : null}

      {body}

      {/* the local player's full-card reveal (we have the CardDefinition only
          for our own pull; others surface as result chips on their card). */}
      <CardRevealOverlay
        open={Boolean(revealed)}
        card={revealed?.card ?? null}
        headline={revealed?.headline}
        onClose={() => {
          setRevealed(null);
          refetch();
          // Closing the reveal is not a mutation (amendment 11) — invalidate the
          // hand-summary tag by hand so the global badge picks up the new card.
          dispatch(respectCardsApi.util.invalidateTags(["HandSummary"]));
        }}
      />

      {showJackpot && jackpotAnim ? (
        <JackpotCelebration
          username={jackpotAnim.username}
          onClose={() => setDismissedJackpot(jackpotAnim.seq)}
        />
      ) : null}
    </div>
    </div>
  );
}
